import { DateTime } from '@mode-switch/express';
import {
  BasicChannel,
  ColorTarget,
  EncField,
  EncFieldGroup,
  Encoding,
  MarkChannel,
  MarkType,
  VariableType,
} from '@mode-vegas/vegas';
import { ChartDomain, ChartTypes, DataProfilingTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField, CompiledFormula } from '@mode/shared/util-switch';
import { createAction, props } from '@ngrx/store';
import { ReportFilterControl } from 'libs/shared/contract-common/src/lib/report';

const createChartSuccessProps = props<{
  chart: ChartDomain.Chart;
  switchViewToken: string;
  queryToken: string;
}>();
const createChartFailureProps = props<{ switchViewToken: string; error: Error }>();

const deleteChartProps = props<{ token: string }>();
const deleteChartSuccessProps = props<{ token: string }>();
const deleteChartFailureProps = props<{
  error: Error;
  token: string;
}>();

const updateChartSuccessProps = props<{ token: string; chart: ChartDomain.Chart }>();
const updateChartFailureProps = props<{
  error: Error;
  token: string;
}>();

const cloneChartProps = props<{ token: string }>();
const cloneChartSuccessProps = props<{
  chart: ChartDomain.Chart;
  queryToken: string;
  switchViewToken: string;
}>();
const cloneChartFailureProps = props<{ token: string; error: Error }>();

const seletedFieldToFormatProps = props<{ field: EncField | null }>();

export const updateFieldFormat = createAction(
  '[Editor Designer] UpdateFieldFormat',
  props<{ token: string; formula: string; pane: ChartTypes.FieldPaneTypes; format: ChartTypes.TextFormat }>()
);

export const updateFormat = createAction(
  '[Editor Designer] UpdateFormat',
  props<{ token: string; format: ChartTypes.ChartFormat }>()
);

/**
 * For creating a new chart from the editor browser component
 */
export const createDefaultChart = createAction(
  '[Editor Browser] CreateChart',
  props<{ switchViewToken: string; queryToken: string }>()
);
export const createDefaultChartSuccess = createAction('[Editor Browser] CreateChartSuccess', createChartSuccessProps);
export const createDefaultChartFailure = createAction('[Editor Browser] CreateChartFailure', createChartFailureProps);

/**
 * For creating a new vegas encoding chart from the editor browser component
 */
export const createVegasEncodingChart = createAction(
  '[Editor Browser] CreateVegasEncodingChart',
  props<{ switchViewToken: string; queryToken: string }>()
);
export const createVegasEncodingChartSuccess = createAction(
  '[Editor Browser] CreateChartSuccess',
  createChartSuccessProps
);
export const createVegasEncodingChartFailure = createAction(
  '[Editor Browser] CreateChartFailure',
  createChartFailureProps
);

/**
 * For creating a new chart from the editor tabs component
 */
export const createNewChart = createAction(
  '[Editor Tabs] CreateChart',
  props<{
    switchViewToken: string;
    queryToken: string;
    chartType: ChartTypes.ChartType;
    encoding?: Encoding;
    format?: ChartTypes.ChartFormat;
    fieldFormats?: ChartTypes.FieldFormatMap;
    isAutoUpdateDisabled?: boolean;
  }>()
);
export const createNewChartSuccess = createAction('[Editor Tabs] CreateChartSuccess', createChartSuccessProps);
export const createNewChartFailure = createAction('[Editor Tabs] CreateChartFailure', createChartFailureProps);

export const createNewTable = createAction(
  '[Editor Tabs] CreateNewTable',
  props<{
    switchViewToken: string;
    queryToken: string;
  }>()
);
export const createNewTableSuccess = createAction('[Editor Tabs] CreateTableSuccess', createChartSuccessProps);
export const createNewTableFailure = createAction('[Editor Tabs] CreateTableFailure', createChartFailureProps);

export const deleteChartFromBrowser = createAction('[Editor Browser] DeleteChart', deleteChartProps);
export const deleteChartFromBrowserFailure = createAction(
  '[Editor Browser] DeleteChartFailure',
  deleteChartFailureProps
);
export const deleteChartFromBrowserSuccess = createAction(
  '[Editor Browser] DeleteChartSuccess',
  deleteChartSuccessProps
);

export const deleteChartFromTabs = createAction('[Editor Tabs] DeleteChart', deleteChartProps);
export const deleteChartFromTabsFailure = createAction('[Editor Tabs] DeleteChartFailure', deleteChartFailureProps);
export const deleteChartFromTabsSuccess = createAction('[Editor Tabs] DeleteChartSuccess', deleteChartSuccessProps);

export const cloneChartFromBrowser = createAction('[Editor Browser] CloneChart', cloneChartProps);
export const cloneChartFromBrowserSuccess = createAction('[Editor Browser] CloneChartSuccess', cloneChartSuccessProps);
export const cloneChartFromBrowserFailure = createAction('[Editor Browser] CloneChartFailure', cloneChartFailureProps);

export const cloneChartFromTab = createAction('[Editor Tab] CloneChart', cloneChartProps);
export const cloneChartFromTabSuccess = createAction('[Editor Tab] CloneChartSuccess', cloneChartSuccessProps);
export const cloneChartFromTabFailure = createAction('[Editor Tab] CloneChartFailure', cloneChartFailureProps);

export const cloneChartFromBuilder = createAction('[Editor Builder] CloneChart', cloneChartProps);
export const cloneChartFromBuilderSuccess = createAction('[Editor Builder] CloneChartSuccess', cloneChartSuccessProps);
export const cloneChartFromBuilderFailure = createAction('[Editor Builder] CloneChartFailure', cloneChartFailureProps);

export const cloneChartFromDesigner = createAction('[Editor Designer] CloneChart', cloneChartProps);
export const cloneChartFromDesignerSuccess = createAction(
  '[Editor Designer] CloneChartSuccess',
  cloneChartSuccessProps
);
export const cloneChartFromDesignerFailure = createAction(
  '[Editor Designer] CloneChartFailure',
  cloneChartFailureProps
);

export const addChartFromVizStorage = createAction(
  '[Editor Designer] AddChartFromVizStorage',
  props<{
    switchViewToken: string;
    queryToken: string;
    snapshot: ChartTypes.ChartSnapshot;
  }>()
);

export const replaceFromVizStorage = createAction(
  '[Editor Designer] ReplaceFromVizStorage',
  props<{
    token: string;
    snapshot: ChartTypes.ChartSnapshot;
  }>()
);

export const updateDesignerChartEncoding = createAction(
  '[Editor Designer] UpdateChartEncoding',
  props<{
    token: string;
    encoding: Encoding;
    format?: Partial<ChartTypes.ChartFormat>;
    facetSizes?: Readonly<ChartTypes.FacetSizeMap>;
    fieldFormats?: Readonly<ChartTypes.FieldFormatMap>;
    fieldColors?: Readonly<ChartTypes.FieldColorMap>;
    colorPaletteToken?: string;
  }>()
);
export const updateDesignerChartEncodingSuccess = createAction(
  '[Editor Designer] UpdateChartEncodingSuccess',
  updateChartSuccessProps
);
export const updateDesignerChartEncodingFailure = createAction(
  '[Editor Designer] UpdateChartEncodingFailure',
  updateChartFailureProps
);

export const updateDesignerChartTitle = createAction(
  '[Editor Designer] UpdateChartTitle',
  props<{
    token: string;
    title: string;
  }>()
);

export const updateDesignerChartType = createAction(
  '[Editor Designer] UpdateChartType',
  props<{
    token: string;
    chartType: ChartTypes.ChartType;
  }>()
);
export const updateDesignerChartTypeSuccess = createAction(
  '[Editor Designer] UpdateChartTypeSuccess',
  updateChartSuccessProps
);

export const updateDesignerChartTypeFailure = createAction(
  '[Editor Designer] UpdateChartTypeFailure',
  updateChartFailureProps
);

export const updateDesignerChartPalette = createAction(
  '[Editor Designer] UpdateChartPalette',
  props<{
    token: string;
    paletteToken: string;
  }>()
);

export const updateDesignerColors = createAction(
  '[Editor Designer] UpdateDesignerColors',
  props<{
    token: string;
    colorPaletteToken: string;
    palette: string[];
    showAlert: boolean;
  }>()
);

export const updateColors = createAction(
  '[Editor Designer] UpdateColors',
  props<{
    token: string;
    planRefID: string;
    colorPaletteToken: string;
    palette: string[];
    showAlert: boolean;
  }>()
);

export const updateChart = createAction(
  '[Editor Designer] UpdateChart',
  props<{
    token: string;
    chart: ChartTypes.ChartSnapshot;
    sourceAction: string;
    suppressTableRefresh?: boolean;
  }>()
);

export const updateChartFromSnapshot = createAction(
  '[Editor Designer] UpdateChartFromSnapshot',
  props<{
    token: string;
    model?: FlamingoTypes.ModeModel;
  }>()
);

// This action doesn't have specific success and failures because it's handled
// by the same effect as `updateChart`
export const updateChartNoEncoding = createAction(
  '[Editor Designer] UpdateChartNoEncoding',
  props<{
    token: string;
    chart: ChartTypes.ChartSnapshot;
    sourceAction: string;
  }>()
);

export const updateChartSuccess = createAction(
  '[Editor Designer] UpdateChartSuccess',
  props<{
    token: string;
    chart: ChartDomain.Chart;
    sourceAction: string;
  }>()
);

export const updateChartFailure = createAction(
  '[Editor Designer] UpdateChartFailure',
  props<{
    token: string;
    error: Error;
    sourceAction: string;
  }>()
);

export const updateIsAutoUpdateDisabled = createAction(
  '[Editor Designer] UpdateIsAutoUpdateDisabled',
  props<{
    token: string;
    isAutoUpdateDisabled: boolean;
  }>()
);

export const manuallyApplyChanges = createAction(
  '[Editor Designer] ManuallyApplyChanges',
  props<{
    token: string;
  }>()
);

export const setUnappliedChangesCount = createAction(
  '[Editor Designer] SetUnappliedChangesCount',
  props<{
    token: string;
    count: number;
  }>()
);

export const setEphemeral = createAction(
  '[Editor Designer] SetEphemeral',
  props<{
    token: string;
    state?: boolean;
  }>()
);

export const clearEphemeral = createAction(
  '[Editor Designer] ClearEphemeral',
  props<{
    token: string;
  }>()
);

export const updateCaption = createAction(
  '[Editor Designer] UpdateCaption',
  props<{
    token: string;
    caption: ChartTypes.CaptionInfo;
  }>()
);

export const clearChart = createAction('[Editor Designer] ClearChart', props<{ token: string }>());
export const swapAxes = createAction('[Editor Designer] SwapAxes', props<{ token: string }>());

export const createChartUndo = createAction(
  '[Editor Designer] CreateChartUndo',
  props<{
    token: string;
  }>()
);

export const createChartRedo = createAction(
  '[Editor Designer] CreateChartRedo',
  props<{
    token: string;
  }>()
);

export const undo = createAction(
  '[Editor Designer] Undo',
  props<{
    token: string;
    snapshot: ChartTypes.ChartSnapshot;
  }>()
);

export const undoTemp = createAction(
  '[Editor Designer] Undo Temp',
  props<{
    token: string;
    snapshot: ChartTypes.ChartSnapshot;
  }>()
);

export const redo = createAction(
  '[Editor Designer] Redo',
  props<{
    token: string;
    snapshot: ChartTypes.ChartSnapshot;
  }>()
);

export const redoTemp = createAction(
  '[Editor Designer] Redo Temp',
  props<{
    token: string;
    snapshot: ChartTypes.ChartSnapshot;
  }>()
);

export const drillDown = createAction(
  '[Editor Designer] drillDown',
  props<{ token: string; target: ChartTypes.MarkTarget }>()
);

export const changeHierarchy = createAction(
  '[Editor Designer] ChangeHierarchy',
  props<{ token: string; forward: boolean }>()
);

export const dynamicDrillDown = createAction(
  '[Editor Designer] DynamicDrillDown',
  props<{
    token: string;
    target: ChartTypes.MarkTarget;
    targetFieldId: string;
    drillToField: string;
    dateTruncUnit?: DateTime.Unit;
  }>()
);

export const updateBigValueDescription = createAction(
  '[Editor Designer] updateBigValueDescription',
  props<{ token: string; description: string }>()
);

export const updateBigValueTrendLabel = createAction(
  '[Editor Designer] updateBigValueTrendLabel',
  props<{ token: string; label: string }>()
);
export const clearFilters = createAction('[Editor Designer] ClearFilters', props<{ token: string }>());
export const clearSorts = createAction('[Editor Designer] ClearSorts', props<{ token: string }>());
export const clearFormatting = createAction('[Editor Designer] ClearFormatting', props<{ token: string }>());
export const quickSort = createAction('[Editor Designer] QuickSort', props<{ token: string; descending: boolean }>());
export const removeSort = createAction('[Editor Designer] RemoveSort', props<{ token: string; formula: string }>());
export const viewCalcModalManualSortDomainRequest = createAction(
  '[View Calc Sort Modal] Get ByManual Sort Domain',
  props<{
    token: string;
    encoding: Encoding;
    refId: string;
    field: EncField;
    items: unknown[] | undefined;
  }>()
);
export const viewCalcModalManualSortDomainRequestFailure = createAction(
  '[View Calc Sort Modal] Get ByManual Sort Domain Failure',
  props<{
    view: FlamingoTypes.ModeView | undefined;
    field: EncField | undefined;
  }>()
);

export const addFieldAndMakeDual = createAction(
  '[Editor Designer] addFieldAndMakeDual',
  props<{
    token: string;
    enc: Encoding;
    field: CompiledFormula;
    channel: BasicChannel;
    leftMark: MarkType;
    rightMark: MarkType;
  }>()
);

export const moveFieldAndMakeDual = createAction(
  '[Editor Designer] moveFieldAndMakeDual',
  props<{
    token: string;
    enc: Encoding;
    field: CompiledField;
    channel: BasicChannel;
    leftMark: MarkType;
    rightMark: MarkType;
  }>()
);

export const updateAxisFieldFormat = createAction(
  '[Editor Designer] UpdateAxisFieldFormat',
  props<{
    format: ChartTypes.AxisFieldFormat;
    varType: VariableType;
    formula: string;
    token: string;
  }>()
);

export const updateBaseFormatDisplayName = createAction(
  '[Editor Designer] UpdateBaseFormatDisplayName',
  props<{
    format: ChartTypes.BaseFieldFormat;
    varType: VariableType;
    formula: string;
    token: string;
  }>()
);

export const updateHeaderFieldFormat = createAction(
  '[Editor Designer] UpdateHeaderFieldFormat',
  props<{
    format: ChartTypes.HeaderFieldFormat;
    varType: VariableType;
    formula: string;
    token: string;
  }>()
);

export const updateLegendFieldFormat = createAction(
  '[Editor Designer] UpdateLegendFieldFormat',
  props<{
    format: ChartTypes.LegendFieldFormat;
    varType: VariableType;
    formula: string;
    token: string;
  }>()
);

export const updatePaneFieldFormat = createAction(
  '[Editor Designer] UpdatePaneFieldFormat',
  props<{
    format: ChartTypes.PaneFieldFormat;
    varType: VariableType;
    formula: string;
    token: string;
  }>()
);

export const updateDesignerFieldColor = createAction(
  '[Editor Designer] UpdateDesignerFieldColor',
  props<{
    token: string;
    formula: string;
    varType: VariableType;
    colorPaletteToken: string;
    palette: string[];
    series?: string;
    colorMap: ChartTypes.ColorEntries;
    gradient?: ChartTypes.ContinuousGradient;
    unassignedColor?: string;
    colorTarget?: ColorTarget;
  }>()
);

export const updateFacetSizes = createAction(
  '[Editor Designer] updateFacetSizes',
  props<{
    facetSizes: ChartTypes.FacetSizeMap;
    token: string;
  }>()
);

export const updateEncodingAndFormat = createAction(
  '[Editor Designer] updateEncodingAndFormat',
  props<{
    token: string;
    encoding: Encoding;
    facetSizes?: Readonly<ChartTypes.FacetSizeMap>;
    fieldFormats?: Readonly<ChartTypes.FieldFormatMap>;
    format?: Partial<ChartTypes.ChartFormat>;
    fieldColors?: Readonly<ChartTypes.FieldColorMap>;
    colorPaletteToken?: string;
  }>()
);

export const replaceChartHistory = createAction(
  '[Editor Designer] replaceChartHistory',
  props<{
    token: string;
    encoding?: Encoding;
    isAutoUpdateDisabled?: boolean;
  }>()
);

export const viewCalcFieldSetFieldToFormat = createAction(
  '[View Calc Field Component] Set Field To Format',
  seletedFieldToFormatProps
);
export const viewCalcFieldAllSetFieldToFormat = createAction(
  '[View Calc Field All Component] Set Field To Format',
  seletedFieldToFormatProps
);
export const vizSettingsComponentRemoveSelectedFieldToFormat = createAction(
  '[Viz Settings Component] Remove Selected Field To Format'
);

export const sharedAxisEncodingSetGroupToFormat = createAction(
  '[Shared Axis Encoding Component] Set Group To Format',
  props<{ group: EncFieldGroup }>()
);

export const filterGetDomainValuesRequest = createAction(
  '[Filter Modal] Get Domain Values',
  props<{
    queryRunToken: string;
    view: FlamingoTypes.ModeView;
    encoding: Encoding;
    field: CompiledField;
    refId: string;
    domainAlias: string;
    withFilters: 'all' | 'none' | 'otherFields';
    series?: string;
  }>()
);

export const colorsGetDomainValuesRequest = createAction(
  '[Edit Colors Modal] Get Domain Values',
  props<{
    queryRunToken: string;
    view: FlamingoTypes.ModeView;
    encoding: Encoding;
    field: CompiledField;
    refId: string;
    domainAlias: string;
    withFilters: 'all' | 'none' | 'otherFields';
    series?: string;
  }>()
);

export const continuousMarkDomainRangeRequest = createAction(
  '[Colors Modal] Get Continuous Domain Range',
  props<{
    queryRunToken: string;
    view: FlamingoTypes.ModeView;
    encoding: Encoding;
    refId: string;
    alias: string;
    channel: MarkChannel;
  }>()
);

export const getDataProfiling = createAction(
  '[Editor Designer] GetDataProfiling',
  props<{
    token: string;
    cols: DataProfilingTypes.DataProfilingColInfo[];
    reportFilters?: ReportFilterControl[];
    isDesignerView?: boolean;
    referenceId?: string;
  }>()
);

export const getBinSize = createAction(
  '[Editor Designer] GetBinSize',
  props<{
    token: string;
    columnName: string;
    isDesignerView?: boolean;
    refId?: string;
  }>()
);

export const importDatasetWithChartSuccess = createAction(
  '[ReportEditor] Import Dataset With Chart Success',
  props<{
    accountId: string;
    reportId: string;
    switchViewToken: string;
    vizToken: string;
    queryToken: string;
    chart: ChartDomain.Chart;
  }>()
);

export type Union =
  | ReturnType<typeof createChartUndo>
  | ReturnType<typeof createChartRedo>
  | ReturnType<typeof createDefaultChart>
  | ReturnType<typeof createDefaultChartFailure>
  | ReturnType<typeof createDefaultChartSuccess>
  | ReturnType<typeof createVegasEncodingChart>
  | ReturnType<typeof createVegasEncodingChartFailure>
  | ReturnType<typeof createVegasEncodingChartSuccess>
  | ReturnType<typeof createNewChart>
  | ReturnType<typeof createNewChartFailure>
  | ReturnType<typeof createNewChartSuccess>
  | ReturnType<typeof createNewTable>
  | ReturnType<typeof createNewTableFailure>
  | ReturnType<typeof createNewTableSuccess>
  | ReturnType<typeof deleteChartFromBrowser>
  | ReturnType<typeof deleteChartFromBrowserFailure>
  | ReturnType<typeof deleteChartFromBrowserSuccess>
  | ReturnType<typeof deleteChartFromTabs>
  | ReturnType<typeof deleteChartFromTabsFailure>
  | ReturnType<typeof deleteChartFromTabsSuccess>
  | ReturnType<typeof cloneChartFromBrowser>
  | ReturnType<typeof cloneChartFromBrowserSuccess>
  | ReturnType<typeof cloneChartFromBrowserFailure>
  | ReturnType<typeof cloneChartFromTab>
  | ReturnType<typeof cloneChartFromTabSuccess>
  | ReturnType<typeof cloneChartFromTabFailure>
  | ReturnType<typeof cloneChartFromBuilder>
  | ReturnType<typeof cloneChartFromBuilderSuccess>
  | ReturnType<typeof cloneChartFromBuilderFailure>
  | ReturnType<typeof cloneChartFromDesigner>
  | ReturnType<typeof cloneChartFromDesignerSuccess>
  | ReturnType<typeof cloneChartFromDesignerFailure>
  | ReturnType<typeof addChartFromVizStorage>
  | ReturnType<typeof replaceFromVizStorage>
  | ReturnType<typeof updateDesignerChartEncoding>
  | ReturnType<typeof updateDesignerChartEncodingFailure>
  | ReturnType<typeof updateDesignerChartEncodingSuccess>
  | ReturnType<typeof updateDesignerChartTitle>
  | ReturnType<typeof updateDesignerChartType>
  | ReturnType<typeof updateDesignerChartTypeFailure>
  | ReturnType<typeof updateDesignerChartTypeSuccess>
  | ReturnType<typeof updateDesignerChartPalette>
  | ReturnType<typeof updateChart>
  | ReturnType<typeof updateChartNoEncoding>
  | ReturnType<typeof updateChartFromSnapshot>
  | ReturnType<typeof updateChartSuccess>
  | ReturnType<typeof updateChartFailure>
  | ReturnType<typeof updateIsAutoUpdateDisabled>
  | ReturnType<typeof manuallyApplyChanges>
  | ReturnType<typeof setUnappliedChangesCount>
  | ReturnType<typeof clearEphemeral>
  | ReturnType<typeof setEphemeral>
  | ReturnType<typeof updateCaption>
  | ReturnType<typeof clearChart>
  | ReturnType<typeof swapAxes>
  | ReturnType<typeof undo>
  | ReturnType<typeof redo>
  | ReturnType<typeof undoTemp>
  | ReturnType<typeof redoTemp>
  | ReturnType<typeof drillDown>
  | ReturnType<typeof changeHierarchy>
  | ReturnType<typeof dynamicDrillDown>
  | ReturnType<typeof updateBigValueDescription>
  | ReturnType<typeof updateBigValueTrendLabel>
  | ReturnType<typeof updateFormat>
  | ReturnType<typeof updateFieldFormat>
  | ReturnType<typeof updateBaseFormatDisplayName>
  | ReturnType<typeof quickSort>
  | ReturnType<typeof removeSort>
  | ReturnType<typeof clearFilters>
  | ReturnType<typeof clearFormatting>
  | ReturnType<typeof updateColors>
  | ReturnType<typeof updateLegendFieldFormat>
  | ReturnType<typeof updateFacetSizes>
  | ReturnType<typeof updateEncodingAndFormat>
  | ReturnType<typeof replaceChartHistory>
  | ReturnType<typeof updateHeaderFieldFormat>
  | ReturnType<typeof updateAxisFieldFormat>
  | ReturnType<typeof updatePaneFieldFormat>
  | ReturnType<typeof updateDesignerFieldColor>
  | ReturnType<typeof clearSorts>
  | ReturnType<typeof viewCalcModalManualSortDomainRequest>
  | ReturnType<typeof viewCalcModalManualSortDomainRequestFailure>
  | ReturnType<typeof viewCalcFieldSetFieldToFormat>
  | ReturnType<typeof viewCalcFieldAllSetFieldToFormat>
  | ReturnType<typeof vizSettingsComponentRemoveSelectedFieldToFormat>
  | ReturnType<typeof sharedAxisEncodingSetGroupToFormat>
  | ReturnType<typeof filterGetDomainValuesRequest>
  | ReturnType<typeof importDatasetWithChartSuccess>
  | ReturnType<typeof colorsGetDomainValuesRequest>
  | ReturnType<typeof continuousMarkDomainRangeRequest>
  | ReturnType<typeof getDataProfiling>
  | ReturnType<typeof getBinSize>;
